/**
 * @file SharePoint group-request workflow.
 *
 * Provides the SharePoint-specific handlers that plug into the user system:
 * - `getCurrentUser` — fetches the current SP user and translates SP groups
 *   into application role-group names via `groupMapping`.
 * - `createNativeGroupRequestHandler` — opens SharePoint's native
 *   `reqgroup.aspx` UI for membership requests.
 * - `createGroupRequestHandler` — full approval workflow backed by an
 *   RxDB collection replicated to a SharePoint list, with email notifications.
 *
 * Built and wired into `useSharePoint()` by `sharePoint.js`. Not exposed
 * directly to consumers.
 */

import { SharePointError } from '../../utils/errors.js';
import { useErrorHandler } from '../../core/errors.js';
import {
  sendNewRequestNotification,
  sendStatusChangeNotification,
} from '../../sharepoint/email.js';
import { useCollection } from '../../collections/composables/collection.js';
import { useUser } from '../../user/composables/user.js';
import SharePointUserGroupRequestController from '../components/SharePointUserGroupRequestController.js';

const { handleError } = useErrorHandler();

export const GROUP_REQUESTS_COLLECTION_SCHEMA = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    requesterName: { type: 'string', maxLength: 255 },
    requesterEmail: { type: 'string', maxLength: 255 },
    requesterId: { type: 'string', maxLength: 255 },
    requestedGroupId: { type: 'integer' },
    requestedGroupName: { type: 'string', maxLength: 255 },
    justification: { type: 'string', maxLength: 1000 },
    status: {
      type: 'string',
      enum: ['pending', 'approved', 'denied'],
      default: 'pending',
    },
    requestedAt: { type: 'string', format: 'date-time' },
    processedAt: { type: 'string', format: 'date-time' },
    processedByName: { type: 'string', maxLength: 255 },
    processedByEmail: { type: 'string', maxLength: 255 },
    adminComments: { type: 'string', maxLength: 1000 },
    action: { type: 'string', enum: ['join', 'leave'] },
    serverMetadata: { type: 'object' },
  },
  required: [
    'id',
    'requesterName',
    'requesterEmail',
    'requesterId',
    'requestedGroupId',
    'requestedGroupName',
    'status',
    'requestedAt',
    'action',
  ],
};

/**
 * Builds the three SharePoint group-request handlers, wired against the
 * shared SharePoint singleton.
 *
 * @param {object} deps
 * @param {{value: object}} deps.client - SharePoint client ref.
 * @param {{value: object}} deps.groupMapping - SP-group-id → app-group-name map.
 * @param {() => Promise<void>} deps.ensureInitialized
 * @param {(options: object) => any} deps.replicateCollection
 * @returns {{
 *   getCurrentUser: () => Promise<object>,
 *   createNativeGroupRequestHandler: (options?: object) => object,
 *   createGroupRequestHandler: (options: object) => object,
 * }}
 */
export function buildGroupRequests({
  client,
  groupMapping,
  ensureInitialized,
  replicateCollection,
}) {
  async function getCurrentUser() {
    await ensureInitialized();
    try {
      const [userInfo, userGroups] = await Promise.all([
        client.value.getCurrentUser(),
        client.value.getCurrentUserGroups(),
      ]);

      const appGroups = [];
      const spGroupIds = userGroups.map(g => g.Id).filter(Boolean);
      Object.entries(groupMapping.value).forEach(([groupName, spGroupId]) => {
        if (spGroupIds.includes(spGroupId)) {
          appGroups.push(groupName);
        }
      });

      return {
        id: userInfo.id,
        name: userInfo.name,
        email: userInfo.email,
        groups: appGroups,
      };
    } catch (err) {
      const error = new SharePointError(
        `Failed to get current user: ${err.message}`,
        'read',
        null,
        err
      );
      await handleError(error, { operation: 'getCurrentUser' });
      throw error;
    }
  }

  function createNativeGroupRequestHandler() {
    return {
      async handleGroupRequest(request) {
        const { userId, groupId, action } = request;
        if (!userId || !groupId || !action) {
          throw new Error('userId, groupId, and action are required');
        }

        const spGroupId = groupMapping.value[groupId];
        if (!spGroupId) {
          throw new Error(`Unknown group: ${groupId}`);
        }

        await ensureInitialized();

        const groupResponse = await client.value.request(
          `web/sitegroups/getbyid(${spGroupId})?$select=Title`,
          'GET'
        );
        const groupName = groupResponse?.d?.Title;
        if (!groupName) {
          throw new Error(`Group with ID ${spGroupId} not found`);
        }

        const baseUrl = client.value.siteUrl;
        const membershipUrl = `${baseUrl}/_layouts/15/reqgroup.aspx?Group=${encodeURIComponent(groupName)}`;
        if (typeof window !== 'undefined' && window.open) {
          window.open(membershipUrl, '_blank');
        } else {
          throw new Error('Cannot open new window: not in browser environment');
        }

        return {
          status: 'opened',
          message: `Opened SharePoint native UI for ${action} request to ${groupId}`,
          redirectUrl: membershipUrl,
        };
      },
    };
  }

  function createGroupRequestHandler(options = {}) {
    const {
      requestsListGuid,
      adminEmail = null,
      collectionName = 'sharepoint-group-requests',
      emailTemplates,
      approvalUrl = '',
    } = options;

    if (!requestsListGuid) {
      throw new Error(
        'requestsListGuid is required for SharePoint group request handler'
      );
    }

    const user = useUser();

    const collection = useCollection({
      name: collectionName,
      schema: GROUP_REQUESTS_COLLECTION_SCHEMA,
      setupReplication: collection =>
        replicateCollection({
          collection,
          listIdentifier: requestsListGuid,
          pullInterval: 10 * 60 * 1000,
        }),
    });

    const allPendingRequests = Vue.computed(() =>
      collection.items.filter(doc => doc.status === 'pending')
    );

    const userPendingRequests = Vue.computed(() =>
      collection.items.filter(
        request =>
          request.requesterId === user.id && request.status === 'pending'
      )
    );

    const allRequests = Vue.computed(() => collection.items);

    const userRequests = Vue.computed(() =>
      collection.items.filter(request => request.requesterId === user.id)
    );

    return {
      allPendingRequests,
      userPendingRequests,
      allRequests,
      userRequests,
      customUserGroupRequestController: Vue.markRaw(
        SharePointUserGroupRequestController
      ),

      async handleGroupRequest(request) {
        const { userId, groupId, action, metadata } = request;
        if (!userId || !groupId || !action) {
          throw new Error('userId, groupId, and action are required');
        }
        if (!user.loggedIn) {
          throw new Error('User must be logged in to perform group requests');
        }

        const spGroupId = groupMapping.value[groupId];
        if (!spGroupId) {
          throw new Error(`Unknown group: ${groupId}`);
        }

        const justification = metadata?.justification || '';
        const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const requestData = {
          id: requestId,
          requesterName: user.name,
          requesterEmail: user.email,
          requesterId: user.id,
          requestedGroupId: spGroupId,
          requestedGroupName: groupId,
          justification,
          status: 'pending',
          requestedAt: new Date().toISOString(),
          action,
        };

        await collection.insert(requestData);

        if (adminEmail) {
          try {
            await ensureInitialized();
            await sendNewRequestNotification(
              client.value,
              adminEmail,
              requestData,
              emailTemplates,
              approvalUrl
            );
          } catch (emailErr) {
            console.warn('Email notification failed:', emailErr);
          }
        }

        return {
          status: 'pending',
          requestId,
          message: `${action} request submitted for ${groupId}`,
        };
      },

      async cancelRequest(requestId) {
        try {
          const request = collection.get(requestId);
          if (
            !request ||
            request.requesterId !== user.id ||
            request.status !== 'pending'
          ) {
            throw new Error('Request not found or cannot be cancelled');
          }
          await collection.remove(requestId);
          return true;
        } catch (err) {
          const error = new SharePointError(
            `Failed to cancel request: ${err.message}`,
            'write',
            requestId,
            err
          );
          await handleError(error, { operation: 'cancelRequest' });
          return false;
        }
      },

      async processRequest(requestId, action, adminUser, adminComments = '') {
        if (!['approve', 'deny'].includes(action)) {
          throw new Error('Action must be "approve" or "deny"');
        }

        const request = collection.get(requestId);
        if (!request || request.status !== 'pending') {
          throw new Error('Request not found or already processed');
        }

        await ensureInitialized();

        if (action === 'approve') {
          if (request.action === 'join') {
            await client.value.addUserToGroup(
              request.requestedGroupId,
              request.requesterId
            );
          } else if (request.action === 'leave') {
            await client.value.removeUserFromGroup(
              request.requestedGroupId,
              request.requesterId
            );
          }
        }

        await collection.update(request.id, {
          status: action === 'approve' ? 'approved' : 'denied',
          processedAt: new Date().toISOString(),
          processedByName: user.name,
          processedByEmail: user.email,
          adminComments,
        });

        try {
          await sendStatusChangeNotification(
            client.value,
            {
              ...request,
              processedAt: new Date().toISOString(),
              processedByName: user.name,
              adminComments,
            },
            action === 'approve' ? 'approved' : 'denied',
            emailTemplates
          );
        } catch (emailErr) {
          console.warn('Email notification failed:', emailErr);
        }
        return true;
      },
    };
  }

  return {
    getCurrentUser,
    createNativeGroupRequestHandler,
    createGroupRequestHandler,
  };
}
